import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Table, Spinner, Button, Badge, OverlayTrigger, Tooltip, Modal, Form } from "react-bootstrap";
import axios from "axios";
import APP_BASE_URL from "../../components/Api/globalApi";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./UserPoints.scss";

const UserPoints = () => {
  const [loading, setLoading] = useState(false);
  const [points, setPoints] = useState([]);
  const [todayPointRate, setTodayPointRate] = useState(null);
  const [sellPoints, setSellPoints] = useState([]);
  const [showSellPointsModal, setShowSellPointsModal] = useState(false);
  const [sellPointsLoading, setSellPointsLoading] = useState(false);
  const [sellPointsError, setSellPointsError] = useState("");
  const [userHistory, setUserHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [incentiveWallet, setIncentiveWallet] = useState(0);
  const [activityWallet, setActivityWallet] = useState(0);

  const [purchaseLoading, setPurchaseLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const isPreviewOnly = location.state?.previewOnly === true;

  const formatValue = (value, prefix = "") => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return `${prefix}${value}`;
    }

    return `${prefix}${Number.isInteger(numericValue) ? numericValue : numericValue.toFixed(2)}`;
  };

  const formatCashbackValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    const normalizedValue = String(value).trim();
    if (!normalizedValue) {
      return "-";
    }

    return normalizedValue.includes("%") ? normalizedValue : `${normalizedValue}%`;
  };

  const fetchPoints = async () => {
    try {
      setLoading(true);
      const res = await axios.post(
        `${APP_BASE_URL}/virtualuser/points/user-points`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("virtualmall_token")}`,
          },
        }
      );
      if (res.data.status) {
        setPoints(res.data.data || []);
        setTodayPointRate(res.data.today_point ?? null);
      } else {
        toast.error(res.data.message || "Failed to load points");
        setPoints([]);
        setTodayPointRate(null);
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Error loading points");
      setPoints([]);
      setTodayPointRate(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserHistory = async () => {
    try {
      const token = localStorage.getItem("virtualmall_token");
      if (!token) return;

      setHistoryLoading(true);
      const res = await axios.get(`${APP_BASE_URL}/virtualuser/sell/history`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.data.status) {
        setUserHistory(res.data.data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchIncentiveWallet = async () => {
    try {
      const token = localStorage.getItem("virtualmall_token");
      if (!token) return;

      const res = await axios.post(
        `${APP_BASE_URL}/virtualuser/incentive/wallet/history`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (res.data.result) {
        setIncentiveWallet(parseFloat(res.data.incentiveWallet) || 0);
        setActivityWallet(parseFloat(res.data.activityWallet) || 0);
      }
    } catch (err) {
      console.error("Error fetching incentive wallet:", err);
    }
  };

  useEffect(() => {
    fetchPoints();
    fetchUserHistory();
    fetchIncentiveWallet();
  }, []);

  const handlePurchaseClick = async (pkg) => {
    const token = localStorage.getItem("virtualmall_token");
    if (!token) {
      toast.info("Please login to purchase points");
      navigate("/virtual_mall_login?redirect=/user-points");
      return;
    }

    const checkTransactionId = async (transactionId) => {
      const response = await axios.post(
        `${APP_BASE_URL}/virtualuser/point-purchase/check-transaction-id`,
        { transaction_id: transactionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    };

    const result = await Swal.fire({
      title: "Purchase Points?",
      html: `
        <div style="margin-bottom: 15px; text-align: center;">
          <p>Do you want to purchase the <strong>${pkg.vmp_point}</strong> point package for <strong>₹${pkg.vmp_amount}</strong>?</p>
          <div style="margin-top: 15px; padding: 10px; background: #fff; border-radius: 8px; display: inline-block;">
            <img src="/payment_qr.png" alt="Payment QR Code" style="max-width: 200px; height: auto;" />
          </div>
          <p style="margin-top: 10px; font-size: 0.9em; color: #cbd5e1;">Please scan this QR code to make the payment before confirming.</p>
          
          <div style="margin-top: 20px; text-align: left;">
            <div style="margin-bottom: 15px;">
              <label for="transaction-id" style="display: block; margin-bottom: 5px; font-size: 0.9em; color: #f8fafc;">Transaction ID (12 Digits): <span style="color: #ef4444">*</span></label>
              <input type="text" id="transaction-id" placeholder="Enter 12-digit transaction ID" maxlength="12" style="display: block; width: 100%; color: #fff; padding: 10px; border: 1px solid #475569; border-radius: 4px; background: #1e293b;" oninput="this.value = this.value.replace(/[^0-9]/g, '')" />
              <small id="transaction-id-check" style="display: block; min-height: 18px; margin-top: 5px;"></small>
            </div>

            <div style="margin-bottom: 15px;">
              <label for="purchase-amount" style="display: block; margin-bottom: 5px; font-size: 0.9em; color: #f8fafc;">Amount: <span style="color: #ef4444">*</span></label>
              <input type="number" id="purchase-amount" value="${pkg.vmp_amount}" placeholder="Enter amount" style="display: block; width: 100%; color: #fff; padding: 10px; border: 1px solid #475569; border-radius: 4px; background: #1e293b;" />
            </div>

            <div style="margin-bottom: 15px;">
              <label for="vm-id" style="display: block; margin-bottom: 5px; font-size: 0.9em; color: #f8fafc;">Virtual Mall ID: <span style="color: #ef4444">*</span></label>
              <input type="text" id="vm-id" placeholder="Enter Virtual Mall ID" value="${pkg.vmp_vmid}" style="display: block; width: 100%; color: #fff; padding: 10px; border: 1px solid #475569; border-radius: 4px; background: #1e293b;" />
            </div>

            <div style="margin-bottom: 15px;">
              <label for="receipt-upload" style="display: block; margin-bottom: 5px; font-size: 0.9em; color: #f8fafc;">Upload Payment Receipt (Image/PDF): <span style="color: #ef4444">*</span></label>
              <input type="file" id="receipt-upload" accept="image/*,application/pdf" style="display: block; width: 100%; color: #fff; padding: 8px; border: 1px solid #475569; border-radius: 4px; background: #1e293b;" />
            </div>
          </div>
        </div>
      `,
      icon: "info",
      showCancelButton: true,
      confirmButtonColor: "#6366F1",
      cancelButtonColor: "#ef4444",
      confirmButtonText: "Yes, Purchase",
      background: "#0f172a",
      color: "#f8fafc",
      backdrop: `rgba(0,0,0,0.8)`,
      didOpen: () => {
        const transactionInput = document.getElementById('transaction-id');
        const checkMessage = document.getElementById('transaction-id-check');
        transactionInput?.addEventListener('blur', async () => {
          const transactionId = transactionInput.value;
          checkMessage.textContent = '';
          checkMessage.style.color = '#cbd5e1';
          if (!/^\d{12}$/.test(transactionId)) return;
          checkMessage.textContent = 'Checking transaction ID…';
          try {
            const response = await checkTransactionId(transactionId);
            if (transactionInput.value !== transactionId) return;
            checkMessage.textContent = response.exists ? 'This transaction ID has already been used.' : 'Transaction ID is available.';
            checkMessage.style.color = response.exists ? '#f87171' : '#4ade80';
          } catch (error) {
            if (transactionInput.value !== transactionId) return;
            checkMessage.textContent = 'Could not check this ID. Please try again.';
            checkMessage.style.color = '#f87171';
          }
        });
      },
      preConfirm: async () => {
        const fileInput = document.getElementById('receipt-upload');
        const transactionId = document.getElementById('transaction-id').value;
        const amount = document.getElementById('purchase-amount').value;
        const vmid = document.getElementById('vm-id').value;

        if (!transactionId || !/^\d{12}$/.test(transactionId)) {
          Swal.showValidationMessage('Transaction ID must be exactly 12 digits (numbers only).');
          return false;
        }
        try {
          const validation = await checkTransactionId(transactionId);
          if (!validation?.status || validation.exists) {
            Swal.showValidationMessage(validation?.message || 'This transaction ID has already been used. Please enter a different ID.');
            return false;
          }
        } catch (error) {
          Swal.showValidationMessage(error.response?.data?.message || 'Could not validate transaction ID. Please try again.');
          return false;
        }
        if (!amount || isNaN(amount) || Number(amount) <= 0) {
          Swal.showValidationMessage('Please enter a valid amount.');
          return false;
        }
        if (!vmid) {
          Swal.showValidationMessage('Please enter Virtual Mall ID.');
          return false;
        }
        if (!fileInput || !fileInput.files || fileInput.files.length === 0) {
          Swal.showValidationMessage('Please upload your payment receipt.');
          return false;
        }

        return {
          file: fileInput.files[0],
          transactionId,
          amount,
          vmid
        };
      }
    });

    if (result.isConfirmed) {
      try {
        const { file, transactionId, amount, vmid } = result.value;
        setPurchaseLoading(true);
        const token = localStorage.getItem("virtualmall_token");

        const formData = new FormData();
        formData.append("package_id", pkg.vmp_id);
        formData.append("point", pkg.vmp_point);
        formData.append("amount", amount);
        formData.append("vmid", vmid);
        formData.append("transaction_id", transactionId);
        formData.append("receipt_image", file);

        const res = await axios.post(
          `${APP_BASE_URL}/virtualuser/point-purchase/create`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (res.data.status) {
          await Swal.fire({
            title: "Submitted!",
            text: "Your purchase request has been submitted for admin approval.",
            icon: "success",
            background: "#0f172a",
            color: "#f8fafc"
          });
          fetchUserHistory();
        } else {
          toast.error(res.data.message || "Submission failed");
        }
      } catch (err) {
        console.error(err);
        toast.error(err.response?.data?.message || "Error submitting purchase request");
      } finally {
        setPurchaseLoading(false);
      }
    }
  };

  const fetchSellPoints = async () => {
    try {
      console.log("Sell points");

      setSellPointsLoading(true);
      setSellPointsError("");
      const res = await axios.get(`${APP_BASE_URL}/sellpoints-user`);
      if (res.data?.status) {
        setSellPoints(res.data.data || []);
      } else {
        const message = res.data?.message || "Failed to load SellPoints";
        setSellPointsError(message);
        setSellPoints([]);
      }
    } catch (err) {
      console.error(err);
      setSellPointsError(err.response?.data?.message || "Error loading SellPoints");
      setSellPoints([]);
    } finally {
      setSellPointsLoading(false);
    }
  };

  const handleSellClick = () => {
    const token = localStorage.getItem("virtualmall_token");
    if (!token) {
      toast.info("Please login to sell points");
      navigate("/virtual_mall_login?redirect=/user-points");
      return;
    }
    setShowSellPointsModal(true);
    fetchSellPoints();
    fetchIncentiveWallet();
    fetchUserHistory();
  };

  const handleSellNow = async (selectedSellPoint) => {
    try {
      const token = localStorage.getItem("virtualmall_token");
      if (!token) {
        toast.error("Please login first");
        return;
      }

      if (parseFloat(selectedSellPoint.point) > activityWallet) {
        toast.error(`Insufficient balance. You need ${selectedSellPoint.point} points, but you only have ${activityWallet.toFixed(2)} points.`);
        return;
      }

      setLoading(true);
      const res = await axios.post(
        `${APP_BASE_URL}/virtualuser/sell/request`,
        {
          sell_point_id: selectedSellPoint.id,
          point: selectedSellPoint.point,
          indian_rupee: selectedSellPoint.indian_rupee,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.data.status) {
        toast.success(res.data.message || "Sell request submitted successfully");
        setShowSellPointsModal(false);
        fetchUserHistory(); // Refresh history
      } else {
        toast.error(res.data.message || "Failed to submit sell request");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Error submitting sell request");
    } finally {
      setLoading(false);
    }
  };

  const latestEntry = points[0] || {};

  const summaryCards = [
    {
      label: "Today's Point Rate",
      value: formatValue(todayPointRate, "Rs. "),
      tone: "cyan",
    },
    {
      label: "Latest Point",
      value: formatValue(latestEntry.vmp_point),
      tone: "lime",
    },
    {
      label: "Latest Amount",
      value: formatValue(latestEntry.vmp_amount, "₹"),
      tone: "yellow",
    },
    {
      label: "Cashback",
      value: formatCashbackValue(latestEntry.vmp_cashback),
      tone: "pink",
    },
  ];

  const tableHeaders = ["Point", "Amount", "VMID", "Cashback", "Purchase"];
  const sellHistoryByPointId = userHistory.reduce((acc, historyItem) => {
    const sellPointId = Number(historyItem.sell_point_id);
    if (!Number.isNaN(sellPointId) && acc[sellPointId] === undefined) {
      acc[sellPointId] = historyItem;
    }
    return acc;
  }, {});
  const previewPurchaseTooltip = (props) => (
    <Tooltip id="preview-purchase-tooltip" {...props}>
      Springway customer can only buy points.
    </Tooltip>
  );

  return (
    <div className="user-points-page">
      <Container className="user-points-container">
        <section className="user-points-hero">
          <div className="user-points-hero__backdrop" />
          <div className="user-points-hero__content">
            <div className="user-points-heading">
              {localStorage.getItem("virtualmall_token") && (
                <span className="user-points-heading__eyebrow" onClick={() => navigate("/virtual_mall_dash")} style={{ cursor: "pointer" }}>Virtual mall Dashboard</span>
              )}
              <h1 className="user-points-heading__title">Points</h1>
            </div>

            <Row className="g-3 user-points-summary" >
              {summaryCards.map((card) => (
                <Col md={6} xl={3} key={card.label}>
                  <div className={`user-points-stat user-points-stat--${card.tone}`} style={{ height: "4rem" }}>
                    <span className="user-points-stat__label">{card.label}</span>
                    <strong className="user-points-stat__value">{card.value}</strong>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </section>
        <Card className="border-0 user-points-table-card">
          <Card.Body className="user-points-table-card__body">
            <div className="user-points-table-card__header">
              <div>
                <div className="user-points-table-card__eyebrow">Wallet Overview</div>
                <h4 className="user-points-table-card__title">Available Points</h4>
              </div>

              <Badge pill className="user-points-table-card__badge">
                {points.length} {points.length === 1 ? "entry" : "entries"}
              </Badge>
            </div>

            <div className="table-responsive user-points-table-shell">
              <Table className="align-middle text-center mb-0 user-points-table">
                <thead>
                  <tr>
                    {tableHeaders.map((head) => (
                      <th key={head}>{head}</th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan="5" className="user-points-table__state">
                        <div className="d-flex flex-column align-items-center gap-2">
                          <Spinner animation="border" variant="light" />
                          <span>Loading data...</span>
                        </div>
                      </td>
                    </tr>
                  )}

                  {!loading && points.length === 0 && (
                    <tr>
                      <td colSpan="5" className="user-points-table__state">No points available</td>
                    </tr>
                  )}

                  {!loading &&
                    points.map((row) => (
                      <tr key={row.vmp_id} className="user-points-table__row">
                        <td className="user-points-table__cell user-points-table__cell--first user-points-table__cell--strong">
                          {row.vmp_point}
                        </td>

                        <td className="user-points-table__cell user-points-table__cell--strong">
                          ₹ {row.vmp_amount}
                        </td>

                        <td className="user-points-table__cell user-points-table__cell--muted">
                          {row.vmp_vmid}
                        </td>

                        <td className="user-points-table__cell">
                          <span className="user-points-table__pill">
                            {formatCashbackValue(row.vmp_cashback)}
                          </span>
                        </td>

                        <td className="user-points-table__cell user-points-table__cell--last">
                          {isPreviewOnly ? (
                            <OverlayTrigger placement="top" overlay={previewPurchaseTooltip}>
                              <span className="user-points-purchase-tooltip-wrap">
                                <Button
                                  size="sm"
                                  className="user-points-purchase-btn"
                                  disabled
                                >
                                  Purchase
                                </Button>
                              </span>
                            </OverlayTrigger>
                          ) : (
                            <Button size="sm" className="user-points-purchase-btn" onClick={() => handlePurchaseClick(row)}>
                              Purchase
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </div>

            <div className="user-points-actions">
              <Button
                onClick={() => navigate("/virtual-mall")}
                className="user-points-action-btn user-points-action-btn--primary"
              >
                NEXT
              </Button>

              <Button
                className="user-points-action-btn user-points-action-btn--secondary"
                onClick={handleSellClick}
              >
                SELL
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Container>

      <Modal
        show={showSellPointsModal}
        onHide={() => setShowSellPointsModal(false)}
        centered
        size="lg"
        dialogClassName="sell-points-modal modal-dialog-scrollable"
      >
        <Modal.Header className="sell-points-modal__header">
          <Modal.Title className="sell-points-modal__title"></Modal.Title>
          <button
            type="button"
            className="sell-points-modal__close"
            aria-label="Close"
            onClick={() => setShowSellPointsModal(false)}
          >
            ×
          </button>
        </Modal.Header>
        <Modal.Body className="sell-points-modal__body">
          {sellPointsLoading && (
            <div className="sell-points-section__state">
              <Spinner animation="border" />
              <span>Loading SellPoints...</span>
            </div>
          )}

          {!sellPointsLoading && sellPointsError && (
            <div className="sell-points-section__state sell-points-section__state--error">
              <span>{sellPointsError}</span>
              <Button size="sm" className="sell-points-retry-btn" onClick={fetchSellPoints}>
                Retry
              </Button>
            </div>
          )}

          {!sellPointsLoading && !sellPointsError && sellPoints.length === 0 && (
            <div className="sell-points-section__state">
              <span>No SellPoints available</span>
            </div>
          )}

          {!sellPointsLoading && !sellPointsError && sellPoints.length > 0 && (
            <div className="table-responsive sell-points-modal-table-shell">
              <div className="d-flex justify-content-between align-items-center mb-3 px-3">
                <span className="text-muted small">Available Options</span>
                <div className="d-flex flex-wrap gap-2 justify-content-end">
                  <Badge bg="info" className="p-2">
                    Incentive Wallet: Rs. {incentiveWallet.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </Badge>
                  <Badge bg="secondary" className="p-2">
                    Activity Wallet: {activityWallet.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </Badge>
                </div>
                <Badge bg="info" className="p-2 d-none">
                  Incentive Wallet: ₹{incentiveWallet.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </Badge>
                <Badge bg="secondary" className="p-2 ms-2 d-none">
                  Activity Wallet: â‚¹{activityWallet.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </Badge>
              </div>
              <Table className="align-middle text-center mb-0 sell-points-modal-table">
                <thead>
                  <tr>
                    <th>Point</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Sell</th>
                  </tr>
                </thead>
                <tbody>
                  {sellPoints.map((item) => {
                    const sellHistoryItem = sellHistoryByPointId[Number(item.id)];
                    const hasSellHistory = Boolean(sellHistoryItem);
                    const isInsufficientBalance = Number(item.point) > activityWallet;
                    const rawSellStatus = hasSellHistory
                      ? String(sellHistoryItem.status || "pending").toLowerCase()
                      : "available";
                    const shouldBlockNewSell = rawSellStatus === "pending";
                    const sellStatus = shouldBlockNewSell ? "pending" : "available";
                    const isActionDisabled = loading || shouldBlockNewSell || isInsufficientBalance;

                    let actionLabel = "Sell Now";
                    if (shouldBlockNewSell) actionLabel = "Pending";
                    if (!shouldBlockNewSell && rawSellStatus === "approved") actionLabel = "Sell Again";
                    if (!shouldBlockNewSell && rawSellStatus === "rejected") actionLabel = "Sell Now";
                    if (!hasSellHistory && isInsufficientBalance) actionLabel = "Insufficient Balance";
                    if (hasSellHistory && !shouldBlockNewSell && isInsufficientBalance) actionLabel = "Insufficient Balance";

                    return (
                      <tr key={item.id}>
                        <td className="sell-points-modal-table__strong">{formatValue(item.point)}</td>
                        <td className="sell-points-modal-table__strong">
                          {Number(item.indian_rupee).toLocaleString("en-IN", {
                            style: "currency",
                            currency: "INR",
                          })}
                        </td>
                        <td>
                          <span className={`sell-points-status-badge sell-points-status-badge--${sellStatus}`}>
                            {shouldBlockNewSell ? "Pending" : "Available"}
                          </span>
                        </td>
                        <td>
                          <Button
                            className={`sell-points-card__action ${isActionDisabled ? 'btn-disabled' : ''}`}
                            onClick={() => handleSellNow(item)}
                            disabled={isActionDisabled}
                            style={isActionDisabled ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                          >
                            {actionLabel}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  {sellPoints.length === 0 && (
                    <tr>
                      <td colSpan="4" className="py-4 text-muted">
                        No points available to sell.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default UserPoints;
